import type {
  DeliveryZone,
  DeliveryZonePoint,
  SupplierListItem,
} from '@choco-admin/types';
import { Button, Card, CardBody, CardHeader, Chip, Input } from '@heroui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiBookOpen, FiCopy, FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';

import { AlertCard } from '../../../../../../components/AlertCard';
import { useGoogleMapsReady } from '../../../../../BuyersPage/hooks';
import { useSupplierDeliveryZones } from '../../../../hooks/useSupplierDeliveryZones';
import {
  readCopiedZone,
  uniqueZoneName,
  writeCopiedZone,
} from '../../../../utils/deliveryZoneClipboard';
import {
  areDeliveryZonesEqual,
  findDeliveryZoneError,
  MIN_ZONE_POINTS,
} from '../../../../utils/deliveryZones';

import { DeliveryZoneLibraryModal } from './DeliveryZoneLibraryModal';
import { DeliveryZonesMap } from './DeliveryZonesMap';

interface DeliveryZonesTabProps {
  supplier?: SupplierListItem;
}

const NO_ZONES: DeliveryZone[] = [];

/**
 * Where CS sets the areas a distributor delivers to (CRM-364).
 *
 * These areas decide whether buyers ever see this distributor. The buyer-app
 * search filters on them directly: `supplierSearchV2` passes the buyer's
 * coordinates as `deliversTo`, which becomes a `geo_shape` "contains" filter
 * against the indexed zones, so a distributor with no area is returned for no
 * location at all. Until now the portal could show the data but not set it.
 *
 * Note the aggregate `isSupplierVerified` predicate in supplierService also
 * requires a zone, but it has no production caller, so it is not the mechanism
 * that matters here.
 *
 * Saving is separate from the rest of the form on purpose. The form's own save
 * submits every field it holds, so folding zones into it would rewrite unrelated
 * values as a side effect of moving a polygon.
 */
export const DeliveryZonesTab: React.FC<DeliveryZonesTabProps> = ({
  supplier,
}) => {
  const mapsReadiness = useGoogleMapsReady();
  const { saveZones, isSaving, saveError, clearSaveError } =
    useSupplierDeliveryZones(supplier?.id);

  const savedZones = supplier?.deliveryZones ?? NO_ZONES;
  const [zones, setZones] = useState<DeliveryZone[]>(savedZones);
  // What this editor started from. The save compares it against the server's
  // current areas so a stale array cannot replace a colleague's work, and the
  // reseed below compares against it to tell a real change from a refetch.
  const [baselineZones, setBaselineZones] =
    useState<DeliveryZone[]>(savedZones);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  // Corners placed so far for a new area. Null means not drawing, which is
  // distinct from an empty list (drawing started, nothing clicked yet).
  const [draftPoints, setDraftPoints] = useState<DeliveryZonePoint[] | null>(
    null
  );
  const [validationError, setValidationError] = useState<string>();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [libraryNotice, setLibraryNotice] = useState<string>();
  // Whatever was last copied, so the paste control only appears when there is
  // something to paste. Read on render rather than held in state, because the
  // copy usually happens on a different distributor's page.
  const [copiedZone, setCopiedZone] = useState<DeliveryZone | null>(() =>
    readCopiedZone()
  );
  const isDrawing = draftPoints !== null;

  const hasUnsavedChanges = useMemo(
    () => JSON.stringify(zones) !== JSON.stringify(baselineZones),
    [zones, baselineZones]
  );

  // Re-seed when a different distributor loads, or when this tab's own save
  // brings back fresh areas.
  //
  // NOT on every refetch. Saving the profile from another tab refetches the
  // whole supplier, and reseeding there threw away an area someone had just
  // drawn, with no way to get it back. Unsaved work outranks a refetch: the
  // working copy stays, and `saveZones` is what catches a server-side change.
  useEffect(() => {
    if (hasUnsavedChanges) {
      // The baseline deliberately stays where it was. It is what the save
      // compares the server against, so moving it forward here would accept
      // whatever the refetch revealed as this editor's own starting point, and
      // the next save would overwrite it. The notice below says so on screen.
      return;
    }

    setZones(savedZones);
    setBaselineZones(savedZones);
    setSelectedIndex(null);
    setDraftPoints(null);
    setValidationError(undefined);
    setLibraryNotice(undefined);
    // Deliberately keyed on the server's areas alone. Including
    // `hasUnsavedChanges` would rerun this the moment the last edit is undone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedZones]);

  // Someone else changed the areas while this tab has unsaved work. Saving would
  // replace theirs with what is on screen, so say so before the save is
  // attempted rather than only refusing it afterwards.
  const hasConflictingChange =
    hasUnsavedChanges && !areDeliveryZonesEqual(savedZones, baselineZones);

  const handleDraftPointAdd = useCallback((point: DeliveryZonePoint) => {
    setDraftPoints((current) => [...(current ?? []), point]);
  }, []);

  const handleFinishDrawing = () => {
    if (!draftPoints) return;

    setZones((current) => {
      const next = [...current, { name: '', points: draftPoints }];
      setSelectedIndex(next.length - 1);
      return next;
    });
    setDraftPoints(null);
  };

  const handleUndoDraftPoint = () => {
    setDraftPoints((current) => (current ? current.slice(0, -1) : current));
  };

  const handleZonePointsChange = useCallback(
    (index: number, points: DeliveryZonePoint[]) => {
      setZones((current) =>
        current.map((zone, i) => (i === index ? { ...zone, points } : zone))
      );
    },
    []
  );

  const handleLibraryAdd = (
    libraryZones: DeliveryZone[],
    skippedNames: string[]
  ) => {
    if (libraryZones.length > 0) {
      setZones((current) => [...current, ...libraryZones]);
      setSelectedIndex(null);
    }

    setLibraryNotice(
      skippedNames.length > 0
        ? `Already added, so skipped: ${skippedNames.join(', ')}.`
        : undefined
    );
    setValidationError(undefined);
  };

  const handleRename = (index: number, name: string) => {
    setZones((current) =>
      current.map((zone, i) => (i === index ? { ...zone, name } : zone))
    );
    setValidationError(undefined);
  };

  const handleDelete = (index: number) => {
    setZones((current) => current.filter((_, i) => i !== index));
    setSelectedIndex(null);
    setValidationError(undefined);
  };

  const handleCopyZone = (zone: DeliveryZone) => {
    writeCopiedZone(zone);
    setCopiedZone(zone);
    setLibraryNotice(
      `${zone.name.trim() || 'The area'} is ready to add to another distributor.`
    );
  };

  /**
   * Adds the copied area to this distributor, renamed if the name is taken.
   *
   * Legacy admin's copy control is the only way to reproduce a hand-drawn
   * boundary on a second distributor. The ready-made library does not cover it,
   * and redrawing it by eye gives a different shape.
   */
  const handlePasteZone = () => {
    const zone = readCopiedZone();
    if (!zone) {
      setCopiedZone(null);
      return;
    }

    setZones((current) => {
      const next = [
        ...current,
        { name: uniqueZoneName(zone.name, current), points: zone.points },
      ];
      setSelectedIndex(next.length - 1);
      return next;
    });
    setLibraryNotice(undefined);
    setValidationError(undefined);
  };

  const handleSave = async () => {
    clearSaveError();

    const firstProblem = zones
      .map((zone, index) =>
        findDeliveryZoneError({
          zone,
          otherZones: zones.filter((_, i) => i !== index),
        })
      )
      .find(Boolean);

    if (firstProblem) {
      setValidationError(firstProblem);
      return;
    }

    setValidationError(undefined);

    // Copied before awaiting: reshaping is still possible while the save runs,
    // and what was sent is what the new baseline has to be.
    const submitted = structuredClone(zones);
    const saved = await saveZones(submitted, baselineZones);

    if (!saved) return;

    // Commit what was written as the new starting point. Without this the
    // baseline stayed at the pre-save areas, so Save remained enabled and the
    // next edit compared the server's saved areas against them and announced a
    // conflict that was this editor's own save.
    //
    // The working copy is left alone: anything reshaped while the save was in
    // flight is newer than what was sent, and `hasUnsavedChanges` picks it up
    // against the new baseline on its own.
    setBaselineZones(submitted);
  };

  const handleDiscard = () => {
    setZones(savedZones);
    setBaselineZones(savedZones);
    setSelectedIndex(null);
    setDraftPoints(null);
    setValidationError(undefined);
    clearSaveError();
  };

  if (!supplier?.id) {
    return (
      <AlertCard
        type="info"
        title="Save the distributor first"
        message="Delivery areas can be set once the distributor exists."
      />
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-medium font-semibold text-foreground">
              Delivery areas
            </h3>
            <p className="text-small text-default-500">
              Buyers only find this distributor when their address falls inside
              one of these areas. A distributor with no area is not offered to
              anyone.
            </p>
          </div>
          {isDrawing ? (
            <Button variant="flat" onPress={() => setDraftPoints(null)}>
              Cancel
            </Button>
          ) : (
            <div className="flex gap-2">
              {copiedZone && (
                <Button
                  variant="flat"
                  startContent={<FiCopy />}
                  onPress={handlePasteZone}
                >
                  Add copied area
                </Button>
              )}
              <Button
                variant="flat"
                startContent={<FiBookOpen />}
                onPress={() => setIsLibraryOpen(true)}
              >
                Add a ready-made area
              </Button>
              <Button
                color="primary"
                variant="flat"
                startContent={<FiPlus />}
                onPress={() => {
                  setSelectedIndex(null);
                  setDraftPoints([]);
                }}
                isDisabled={mapsReadiness !== 'ready'}
              >
                Draw an area
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody className="space-y-4">
          {mapsReadiness === 'unavailable' && (
            <AlertCard
              type="warning"
              title="The map could not load"
              message="Delivery areas need Google Maps. Check your connection and reload the page."
            />
          )}

          {mapsReadiness === 'ready' && (
            <>
              {isDrawing && (
                <div className="flex flex-wrap items-center gap-3 rounded-lg bg-primary-50 p-3">
                  <p className="text-small text-primary-700">
                    Click the map to place a corner. Three corners make an area.
                    <span className="ml-1 font-medium">
                      {draftPoints.length} placed.
                    </span>
                  </p>
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="light"
                      onPress={handleUndoDraftPoint}
                      isDisabled={draftPoints.length === 0}
                    >
                      Undo last corner
                    </Button>
                    <Button
                      size="sm"
                      color="primary"
                      onPress={handleFinishDrawing}
                      isDisabled={draftPoints.length < MIN_ZONE_POINTS}
                    >
                      Finish area
                    </Button>
                  </div>
                </div>
              )}
              <DeliveryZonesMap
                zones={zones}
                selectedIndex={selectedIndex}
                draftPoints={draftPoints}
                onDraftPointAdd={handleDraftPointAdd}
                onZonePointsChange={handleZonePointsChange}
                onZoneSelect={setSelectedIndex}
              />
            </>
          )}

          {zones.length === 0 ? (
            <p className="text-small text-default-500">
              No delivery areas yet. Pick a ready-made one or draw it, so buyers
              in that area can find this distributor.
            </p>
          ) : (
            <ul className="space-y-2">
              {zones.map((zone, index) => (
                <li
                  // Position is the only stable handle: names are editable and
                  // start empty, so they cannot key the list.
                  key={index}
                  className={`flex items-center gap-3 rounded-lg border p-3 ${
                    index === selectedIndex
                      ? 'border-primary bg-primary-50'
                      : 'border-default-200'
                  }`}
                >
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label={`Show ${zone.name || 'the unnamed area'} on the map`}
                    onPress={() => setSelectedIndex(index)}
                  >
                    <FiMapPin />
                  </Button>
                  <Input
                    aria-label={`Name of area ${index + 1}`}
                    placeholder="Area name, e.g. Berlin Mitte"
                    value={zone.name}
                    onValueChange={(name) => handleRename(index, name)}
                    onFocus={() => setSelectedIndex(index)}
                    size="sm"
                    className="max-w-xs"
                  />
                  <Chip size="sm" variant="flat">
                    {zone.points.length} corners
                  </Chip>
                  {index === selectedIndex && (
                    <span className="text-tiny text-default-500">
                      Drag the dots on the map to reshape
                    </span>
                  )}
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    aria-label={`Copy ${zone.name || 'the unnamed area'} to add to another distributor`}
                    onPress={() => handleCopyZone(zone)}
                  >
                    <FiCopy />
                  </Button>
                  <Button
                    isIconOnly
                    size="sm"
                    variant="light"
                    color="danger"
                    aria-label={`Remove ${zone.name || 'the unnamed area'}`}
                    onPress={() => handleDelete(index)}
                  >
                    <FiTrash2 />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {hasConflictingChange && (
            <AlertCard
              type="warning"
              title="Someone else changed these areas"
              message="Reload the page to see their changes. Your unsaved work here cannot be saved on top of them."
            />
          )}

          {libraryNotice && (
            <AlertCard
              type="info"
              title="Some areas were already there"
              message={libraryNotice}
            />
          )}
          {validationError && (
            <AlertCard
              type="warning"
              title="This area cannot be saved yet"
              message={validationError}
            />
          )}
          {saveError && (
            <AlertCard type="error" title="Saving failed" message={saveError} />
          )}

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="light"
              onPress={handleDiscard}
              isDisabled={!hasUnsavedChanges || isSaving}
            >
              Discard changes
            </Button>
            <Button
              color="primary"
              onPress={handleSave}
              isLoading={isSaving}
              // Saving mid-draw would drop the corners placed so far without
              // saying so.
              isDisabled={!hasUnsavedChanges || isDrawing}
            >
              Save delivery areas
            </Button>
          </div>
        </CardBody>
      </Card>

      <DeliveryZoneLibraryModal
        isOpen={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        supplierLocale={supplier.locale}
        existingZones={zones}
        onAdd={handleLibraryAdd}
      />
    </div>
  );
};
